import React from "react";
import { getWorkspaceInitials } from "../../atoms/WorkspaceInitialsBadge";
import {
  InitialsAvatar,
  OverflowAvatar,
  PhotoAvatar,
  Row,
  Slot,
} from "./AvatarStack.styles";
import type { AvatarStackMember, AvatarStackProps } from "./AvatarStack.types";
import { hideBrokenImage } from "../../../utils/hideBrokenImage";

const DEFAULT_MAX_VISIBLE = 4;
const DEFAULT_SIZE = 28;


/** One slot: initials underneath, the photo (when there is one) layered over them. */
const renderAvatar = (
  member: AvatarStackMember,
  size: number,
): React.ReactElement => (
  <InitialsAvatar $size={size} role="img" aria-label={member.name}>
    <span aria-hidden="true">{getWorkspaceInitials(member.name)}</span>
    {member.avatarUrl ? (
      <PhotoAvatar
        key={member.avatarUrl}
        src={member.avatarUrl}
        alt=""
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={hideBrokenImage}
      />
    ) : null}
  </InitialsAvatar>
);

/**
 * Stacked, overlapping circular avatars with a trailing "+N" overflow chip when
 * the total membership exceeds `maxVisible`. Stateless — all data via props.
 */
export const AvatarStack: React.FC<AvatarStackProps> = ({
  members,
  maxVisible = DEFAULT_MAX_VISIBLE,
  size = DEFAULT_SIZE,
  totalCount,
  ariaLabel,
}) => {
  const total = totalCount ?? members.length;
  const visible = members.slice(0, maxVisible);
  const overflow = Math.max(0, total - visible.length);
  const hasOverflow = overflow > 0;
  const lastVisibleIndex = visible.length - 1;

  return (
    <Row aria-label={ariaLabel ?? `${total} member${total === 1 ? "" : "s"}`}>
      {visible.map((member, idx) => (
        <Slot
          key={member.id}
          $size={size}
          $isLast={!hasOverflow && idx === lastVisibleIndex}
        >
          {renderAvatar(member, size)}
        </Slot>
      ))}
      {hasOverflow && (
        <Slot $size={size} $isLast>
          <OverflowAvatar $size={size} aria-label={`${overflow} more`}>
            {`+${overflow}`}
          </OverflowAvatar>
        </Slot>
      )}
    </Row>
  );
};

export default AvatarStack;
